import { CheckCircleIcon, FileIcon, WarningIcon, ArrowRightIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

import { Breadcrumb } from 'components/Breadcrumb';
import { Button } from 'components/button/Button';
import { PageHeader } from 'components/PageHeader';
import { PageHeading } from 'components/PageHeading';
import { Dropzone } from '../../../../components/Dropzone';
import { Spinner } from '../../../../components/Spinner';
import { useTranslation } from '../../../../contexts/translation-context';
import { getDisplayError } from '../../../../utils/get-display-error';
import { useCustomerPortalCustomer } from '../../CustomerPortalCustomerContext';
import { createOrderFromPdf } from '../models';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';

interface UploadedFile {
  file: globalThis.File;
  documentId: string | null;
  status: 'uploading' | 'uploaded' | 'processing' | 'done' | 'error';
  orderId?: string;
  customerRef?: string;
  error?: string;
}

async function uploadFile(file: globalThis.File): Promise<string> {
  const formData = new FormData();
  formData.set('name', file.name);
  formData.set('file', file);

  const result = await fetch('/api/document/upload', {
    body: formData,
    method: 'post',
  });

  const text = await result.text();
  if (!result.ok) {
    throw new Error(text);
  }

  return JSON.parse(text).data.id;
}

export const CustomerPortalAutomaticCreatePage = () => {
  const { i18n } = useTranslation();
  const { customer } = useCustomerPortalCustomer();
  const [files, setFiles] = useState<globalThis.File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const hasResults = uploadedFiles.some((f) => f.status === 'done' || f.status === 'error');
  const allDone = uploadedFiles.length > 0 && uploadedFiles.every((f) => f.status === 'done' || f.status === 'error');
  const successCount = uploadedFiles.filter((f) => f.status === 'done').length;

  const handleCreateOrders = async () => {
    if (files.length === 0) {
      toast.error(i18n('customerPortal.orders.uploadAtLeastOnePdf'));
      return;
    }

    setIsProcessing(true);

    // Initialize upload state for all files
    const initialState: UploadedFile[] = files.map((file) => ({
      file,
      documentId: null,
      status: 'uploading',
    }));
    setUploadedFiles(initialState);

    // Process each file sequentially to avoid overloading
    for (let i = 0; i < files.length; i++) {
      const file = files[i]!;

      try {
        // Step 1: Upload the file
        const documentId = await uploadFile(file);

        setUploadedFiles((prev) => prev.map((f, idx) => (idx === i ? { ...f, documentId, status: 'processing' } : f)));

        // Step 2: Process with AI
        const result = await createOrderFromPdf(documentId, customer.id);

        trackEvent('customer_portal_order_created_automatic', {
          order_id: result.orderId,
          customer_id: customer.id,
        });

        setUploadedFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, status: 'done', orderId: result.orderId, customerRef: result.customerRef } : f,
          ),
        );
      } catch (err: any) {
        captureException(err);
        setUploadedFiles((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'error', error: getDisplayError(err) } : f)),
        );
      }
    }

    setIsProcessing(false);

    const finalSuccessCount = files.length; // Will be updated by state
    if (finalSuccessCount > 0) {
      toast.success(i18n('customerPortal.orders.orderCreated'));
    }
  };

  return (
    <>
      <PageHeader title={i18n('customerPortal.orders.automatic')} />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: i18n('customerPortal.orders.orders'),
              to: '../..',
            }}
            currentItem={i18n('customerPortal.orders.automatic')}
          />
        }
      />

      <div className="px-8 mb-8 max-w-3xl">
        <h2 className="heading-two mb-2">{i18n('customerPortal.orders.uploadPdfs')}</h2>
        <p className="text-fg-muted mb-4">{i18n('customerPortal.orders.automaticDescription')}</p>

        {!hasResults && (
          <>
            <div className="mb-4">
              <Dropzone files={files} onUpload={setFiles} allowMultiple={true} accept="application/pdf" />
            </div>

            <div className="flex justify-end">
              <Button
                color="primary"
                onTrigger={handleCreateOrders}
                isDisabled={isProcessing || files.length === 0}
                isLoading={isProcessing}
              >
                {isProcessing
                  ? i18n('customerPortal.orders.creatingOrders')
                  : i18n('customerPortal.orders.createOrders')}
              </Button>
            </div>
          </>
        )}

        {uploadedFiles.length > 0 && (
          <div className="mt-4">
            {uploadedFiles.map((uf, idx) => (
              <div key={idx} className="flex items-center gap-3 py-3 border-b border-border last:border-b-0">
                <FileIcon className="w-5 h-5 text-fg-muted shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{uf.file.name}</div>
                  {uf.status === 'done' && uf.customerRef && (
                    <div className="text-sm text-fg-muted">{uf.customerRef}</div>
                  )}
                  {uf.status === 'error' && uf.error && <div className="text-sm text-negative-text">{uf.error}</div>}
                </div>
                <div className="shrink-0">
                  {(uf.status === 'uploading' || uf.status === 'processing') && <Spinner size={4} />}
                  {uf.status === 'done' && (
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="w-5 h-5 text-positive-text" />
                      {uf.orderId && (
                        <Link
                          to={`../../${uf.orderId}`}
                          className="text-sm text-brand hover:underline flex items-center gap-1"
                        >
                          {i18n('customerPortal.orders.viewOrder')}
                          <ArrowRightIcon className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  )}
                  {uf.status === 'error' && <WarningIcon className="w-5 h-5 text-negative-text" />}
                </div>
              </div>
            ))}

            {allDone && successCount > 0 && (
              <div className="mt-4 p-3 bg-positive-subtle rounded text-positive-text text-sm">
                {i18n('customerPortal.orders.ordersCreated', { count: successCount.toString() })}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
